import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateInvite, CreateStaff, Staff, StaffInvite, UpdateStaff } from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * Team management mutations (WS6). The list itself comes from `useStaff` in data/salon.ts; these
 * cover create/edit/deactivate/invite. Every write invalidates the staff list so the roster, the
 * diary chair list, and the offline replica stay consistent.
 */

export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateStaff): Promise<Staff> =>
      // The generated body type over-requires default-valued fields (engagement/bookOverride/
      // onlineBookable); cast at the boundary — the server re-validates against the same Zod schema.
      unwrap(await api.POST('/v1/staff', { body: body as never })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staff }),
  });
}

export function useUpdateStaff() {
  const qc = useQueryClient();
  return useMutation({
    // `active: false` deactivates a member (no DELETE — staff are soft-deactivated, never erased).
    mutationFn: async (input: { id: string; body: UpdateStaff }): Promise<Staff> =>
      unwrap(
        await api.PATCH('/v1/staff/{id}', {
          params: { path: { id: input.id } },
          body: input.body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.staff }),
  });
}

export function useInviteStaff() {
  return useMutation({
    // Emails the member a set-password link. The response carries invite metadata only — never the
    // raw token (it leaves the server through the email transport alone).
    mutationFn: async (input: { id: string; body?: CreateInvite }): Promise<StaffInvite> =>
      unwrap(
        await api.POST('/v1/staff/{id}/invite', {
          params: { path: { id: input.id } },
          body: (input.body ?? {}) as never,
        }),
      ),
  });
}
